import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, EQUIPMENT_LIST, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    name: user?.name || "", phone: "", age: "", height_cm: "", weight_kg: "", goals: "", injury: "",
  });
  const [equipment, setEquipment] = useState(["bodyweight"]);
  const [questions, setQuestions] = useState(null);
  const [scale, setScale] = useState([]);
  const [answers, setAnswers] = useState({});
  const [qIdx, setQIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/me/questions").then((r) => {
      setQuestions(r.data.questions);
      setScale(r.data.scale);
    }).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const toggleEquip = (id) =>
    setEquipment((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/me/onboarding", {
        name: profile.name.trim(), phone: profile.phone.trim(),
        age: profile.age === "" ? null : Number(profile.age),
        height_cm: Number(profile.height_cm), weight_kg: Number(profile.weight_kg),
        goals: profile.goals, injury: profile.injury.trim(), equipment,
      });
      await api.post("/me/assessments", { answers });
      toast.success("Baseline set. Your Capability Scorecard is live.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  const phoneOk = !profile.phone.trim() || /^[0-9+\-\s]{7,16}$/.test(profile.phone.trim());
  const ageOk = profile.age === "" || (Number(profile.age) >= 5 && Number(profile.age) <= 100);
  const profileValid = profile.name.trim() && Number(profile.height_cm) > 50 && Number(profile.weight_kg) > 20 && phoneOk && ageOk;

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70">
        <Wordmark className="text-2xl" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
          Onboarding — step {step + 1} of 3
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="profile" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.35, ease: EASE }}
              className="w-full max-w-xl" data-testid="onboarding-profile">
              <h1 className="font-display uppercase text-5xl leading-[0.9]">First, <span className="text-volt">the basics.</span></h1>
              <div className="mt-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Name *</p>
                    <input data-testid="ob-name" required value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Your full name"
                      className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3.5 focus:border-volt outline-none" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Email *</p>
                    <input data-testid="ob-email" value={user?.email || ""} readOnly
                      className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3.5 text-zinc-500 outline-none cursor-not-allowed" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Phone number (Optional)</p>
                    <input data-testid="ob-phone" value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+91 …"
                      className={`w-full bg-zinc-950 border px-4 py-3.5 focus:border-volt outline-none ${phoneOk ? "border-zinc-700" : "border-red-500"}`} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Age (Optional)</p>
                    <input data-testid="ob-age" type="number" min="5" max="100" value={profile.age}
                      onChange={(e) => setProfile({ ...profile, age: e.target.value })} placeholder="e.g. 32"
                      className={`w-full bg-zinc-950 border px-4 py-3.5 focus:border-volt outline-none ${ageOk ? "border-zinc-700" : "border-red-500"}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Height (cm) *</p>
                    <input data-testid="ob-height" type="number" value={profile.height_cm}
                      onChange={(e) => setProfile({ ...profile, height_cm: e.target.value })} placeholder="170"
                      className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3.5 focus:border-volt outline-none" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Weight (kg) *</p>
                    <input data-testid="ob-weight" type="number" value={profile.weight_kg}
                      onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value })} placeholder="70"
                      className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3.5 focus:border-volt outline-none" />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Injury / physical limitation (Optional)</p>
                  <textarea data-testid="ob-injury" rows={2} value={profile.injury}
                    onChange={(e) => setProfile({ ...profile, injury: e.target.value })}
                    placeholder="Tell us about any injury, pain, movement restriction or physical limitation we should be aware of."
                    className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Your goals</p>
                  <textarea data-testid="ob-goals" rows={2} value={profile.goals}
                    onChange={(e) => setProfile({ ...profile, goals: e.target.value })}
                    placeholder="e.g. Move without pain, carry my kids, hike on weekends…"
                    className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                </div>
                <button data-testid="ob-next-1" disabled={!profileValid} onClick={() => setStep(1)}
                  className="group inline-flex items-center gap-2 bg-volt text-black uppercase tracking-[0.18em] text-xs font-extrabold px-8 py-4 hover:bg-white transition-colors disabled:opacity-40">
                  Continue <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="equip" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.35, ease: EASE }}
              className="w-full max-w-xl" data-testid="onboarding-equipment">
              <h1 className="font-display uppercase text-5xl leading-[0.9]">What can you <span className="text-volt">train with?</span></h1>
              <p className="text-sm text-zinc-400 mt-3">Your sessions are generated only from equipment you have access to.</p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EQUIPMENT_LIST.map((e) => (
                  <button key={e.id} data-testid={`ob-equip-${e.id}`} onClick={() => toggleEquip(e.id)}
                    className={`border px-3 py-4 text-xs uppercase tracking-[0.12em] font-bold transition-colors ${equipment.includes(e.id) ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-300 hover:border-volt"}`}>
                    {e.label}
                  </button>
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <button data-testid="ob-back-1" onClick={() => setStep(0)}
                  className="inline-flex items-center gap-2 border border-zinc-700 px-6 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:border-volt hover:text-volt transition-colors">
                  <ArrowLeft size={14} /> Back
                </button>
                <button data-testid="ob-next-2" disabled={!equipment.length} onClick={() => setStep(2)}
                  className="group inline-flex items-center gap-2 bg-volt text-black uppercase tracking-[0.18em] text-xs font-extrabold px-8 py-4 hover:bg-white transition-colors disabled:opacity-40">
                  Continue <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && questions && (
            <motion.div key={`q-${qIdx}`} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.35, ease: EASE }}
              className="w-full max-w-2xl" data-testid="onboarding-assessment">
              <div className="flex items-center justify-between mb-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
                <span>Functional assessment — {qIdx + 1} of {questions.length}</span>
                <span>{Math.round((Object.keys(answers).length / questions.length) * 100)}%</span>
              </div>
              <div className="h-1 bg-zinc-800 mb-10">
                <div className="h-full bg-volt transition-all duration-500"
                  style={{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }} />
              </div>
              <h1 className="font-display uppercase text-4xl sm:text-5xl leading-[0.95]" data-testid="ob-question">
                {questions[qIdx].text}
              </h1>
              <div className="mt-8 flex flex-col gap-3">
                {scale.map((s) => (
                  <button key={s.value} data-testid={`ob-answer-${s.value}`}
                    onClick={() => {
                      const next = { ...answers, [questions[qIdx].key]: s.value };
                      setAnswers(next);
                      if (qIdx < questions.length - 1) setQIdx(qIdx + 1);
                    }}
                    className={`border px-6 py-4 text-left font-display uppercase text-xl tracking-wide transition-colors ${answers[questions[qIdx].key] === s.value ? "bg-volt text-black border-volt" : "border-zinc-700 hover:bg-volt hover:text-black hover:border-volt"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="mt-8 flex justify-between items-center">
                <button data-testid="ob-q-back" onClick={() => (qIdx === 0 ? setStep(1) : setQIdx(qIdx - 1))}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
                  <ArrowLeft size={14} /> Back
                </button>
                {Object.keys(answers).length === questions.length && (
                  <button data-testid="ob-finish" onClick={finish} disabled={loading}
                    className="bg-volt text-black uppercase tracking-[0.18em] text-xs font-extrabold px-8 py-4 hover:bg-white transition-colors disabled:opacity-50">
                    {loading ? "Building your scorecard…" : "Build my scorecard"}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
